import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import toast from "react-hot-toast";
import { plaidIdvConnect, plaidIdvSuccess } from "../../../api";
import useSalesFormStore from "../../stores/sales-form";
import {
  findIdvRow,
  identityKey,
  isSameIdentity,
  IDV_FAILED,
  IDV_PASSED,
  IDV_PENDING_REVIEW,
} from "../IdvStatusBadge/IdvStatusBadge";

// Plaid Link onEvent names that carry the session's result. onSuccess alone
// only means the user submitted everything, not that they passed.
const IDV_EVENT_OUTCOMES = {
  IDENTITY_VERIFICATION_PASS_SESSION: IDV_PASSED,
  IDENTITY_VERIFICATION_FAIL_SESSION: IDV_FAILED,
  IDENTITY_VERIFICATION_PENDING_REVIEW_SESSION: IDV_PENDING_REVIEW,
};

// Plaid doesn't guarantee the result event lands before onSuccess, so give it
// a moment before reporting the session without one.
const IDV_OUTCOME_WAIT_MS = 3000;

// How long Plaid Link may take to load before the session is abandoned: a
// blocked or unreachable cdn.plaid.com never reports an error of its own.
const IDV_LINK_LOAD_TIMEOUT_MS = 30000;

/**
 * Headless launcher for Plaid Link (Identity Verification). Mounted while a
 * session is in progress; opens as soon as the SDK is ready.
 */
const PlaidIdvLinkLauncher = ({ token, onSuccess, onExit, onEvent, onLoadError }) => {
  const { open, ready, error } = usePlaidLink({ token, onSuccess, onExit, onEvent });

  useEffect(() => {
    if (ready) {
      open();
    }
  }, [ready, open]);

  // react-plaid-link only logs a script that fails to load, so without this the
  // session would wait forever and block every Verify ID button.
  useEffect(() => {
    if (error) {
      onLoadError();
      return undefined;
    }
    if (ready) {
      return undefined;
    }
    const timer = setTimeout(onLoadError, IDV_LINK_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [error, ready, onLoadError]);

  return null;
};

/**
 * Plaid Identity Verification for the owners on one application, one session
 * at a time. Owners are identified by { first_name, last_name, email }; only
 * powerapp_plaid_idv is written, never the owner records themselves.
 *
 * Render the returned `launcher` once, anywhere in the page.
 */
const usePlaidIdv = ({ editslug, otpCode, isKurvDomain, rows }) => {
  const [linkToken, setLinkToken] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  // identityKey → { outcome, reportedOutcome } for sessions completed on this
  // page; these take precedence over the rows loaded with the application.
  const [localOutcomes, setLocalOutcomes] = useState({});
  // Set synchronously when a session is reserved and cleared when it ends, so a
  // double click — or a second owner's button — cannot start a rival session.
  const busyRef = useRef(false);
  // The session in progress. Plaid Link keeps the callbacks it was created
  // with, so they read the session from here rather than from state.
  const sessionRef = useRef(null);
  // Result reported by Plaid Link's onEvent for the session in progress
  const outcomeRef = useRef(null);
  // Set from onSuccess until the completion request settles, so the exit that
  // tearing Link down can fire does not reset a session mid-save.
  const completingRef = useRef(false);

  const getState = useCallback(
    (identity) => {
      const local = localOutcomes[identityKey(identity)];
      if (local) {
        return local;
      }

      const row = findIdvRow(rows, identity);
      return {
        // A completed session with no outcome is awaiting review, not passed.
        outcome: row ? row.outcome || IDV_PENDING_REVIEW : null,
        reportedOutcome: row?.reported_outcome || null,
      };
    },
    [localOutcomes, rows],
  );

  const endSession = () => {
    busyRef.current = false;
    sessionRef.current = null;
    outcomeRef.current = null;
    setLinkToken(null);
    setLoadingKey(null);
  };

  /**
   * Start a session for `identity`. `validate` (optional, async) runs once the
   * session is reserved; returning false cancels it.
   */
  const start = async (identity, key, validate = null) => {
    if (busyRef.current) {
      return;
    }
    if (!editslug) {
      toast.error("Save the application before verifying ID.");
      return;
    }

    busyRef.current = true;
    setLoadingKey(key);
    outcomeRef.current = null;

    try {
      if (validate && !(await validate())) {
        endSession();
        return;
      }

      const response = await plaidIdvConnect(
        {
          editslug,
          ...identity,
          branding: isKurvDomain ? "KURV" : "PaymentCloud",
        },
        { headers: { OtpCode: otpCode } },
      );

      if (response?.status && response?.data?.link_token) {
        sessionRef.current = {
          plaidId: response.data.plaid_id,
          identity,
          editslug,
          otpCode,
        };
        setLinkToken(response.data.link_token);
      } else {
        toast.error(
          response?.message || "Failed to start ID verification. Please try again.",
        );
        endSession();
      }
    } catch (error) {
      toast.error(
        error?.data?.message || "Failed to start ID verification. Please try again.",
      );
      endSession();
    }
  };

  const waitForOutcome = async () => {
    const deadline = Date.now() + IDV_OUTCOME_WAIT_MS;
    while (!outcomeRef.current && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return outcomeRef.current;
  };

  const handleEvent = useCallback((eventName) => {
    if (IDV_EVENT_OUTCOMES[eventName]) {
      outcomeRef.current = IDV_EVENT_OUTCOMES[eventName];
    }
  }, []);

  const handleSuccess = useCallback(async (_publicToken, metadata) => {
    const session = sessionRef.current;
    const linkSessionId = metadata?.link_session_id;
    completingRef.current = true;

    try {
      if (!session || !linkSessionId) {
        toast.error("ID verification did not return a session. Please try again.");
        return;
      }

      // Link stays mounted while this waits, so a result event that arrives
      // just after onSuccess is still delivered.
      const reportedOutcome = await waitForOutcome();
      const response = await plaidIdvSuccess(
        {
          editslug: session.editslug,
          plaid_id: session.plaidId,
          link_session_id: linkSessionId,
          outcome: reportedOutcome,
        },
        { headers: { OtpCode: session.otpCode } },
      );

      if (!response?.status) {
        toast.error(response?.message || "ID verification failed. Please try again.");
        return;
      }

      // The server keeps the session under review until EMS has the report;
      // reported_outcome is only what Plaid told this browser.
      const state = {
        outcome: response.data?.outcome || IDV_PENDING_REVIEW,
        reportedOutcome: response.data?.reported_outcome || null,
      };
      setLocalOutcomes((prev) => ({ ...prev, [identityKey(session.identity)]: state }));

      // Also record it where the rest of the form reads results — the review
      // step, and this button after a step change remounts it — rather than
      // refetching, which would reinitialise the form. Before OTP there is no
      // application data yet; the server still refuses a second session.
      const { applicationData, setApplicationData } = useSalesFormStore.getState();
      if (applicationData) {
        const row = {
          ...session.identity,
          status: "completed",
          outcome: state.outcome,
          reported_outcome: state.reportedOutcome,
        };
        setApplicationData({
          ...applicationData,
          powerapp_plaid_idv: [
            ...(applicationData.powerapp_plaid_idv || []).filter(
              (existing) => !isSameIdentity(existing, row),
            ),
            row,
          ],
        });
      }

      if (state.reportedOutcome === IDV_FAILED) {
        toast.error("Plaid couldn't verify this ID. You can try again.");
      } else {
        toast.success("ID submitted. It's now under review.");
      }
    } catch (error) {
      toast.error(
        error?.data?.message || "Failed to save ID verification. Please try again.",
      );
    } finally {
      completingRef.current = false;
      endSession();
    }
  }, []);

  const handleExit = useCallback((error) => {
    // Ignore the exit fired by tearing Link down after onSuccess: the
    // completion request owns the session until it settles.
    if (completingRef.current || !sessionRef.current) {
      return;
    }
    endSession();
    if (error) {
      toast.error("ID verification was not completed.");
    }
  }, []);

  const handleLoadError = useCallback(() => {
    if (completingRef.current || !sessionRef.current) {
      return;
    }
    endSession();
    toast.error(
      "Couldn't load Plaid. Check your connection or turn off content blockers, then try again.",
    );
  }, []);

  const launcher = linkToken ? (
    <PlaidIdvLinkLauncher
      token={linkToken}
      onSuccess={handleSuccess}
      onExit={handleExit}
      onEvent={handleEvent}
      onLoadError={handleLoadError}
    />
  ) : null;

  return { getState, start, loadingKey, isKurvDomain, launcher };
};

export default usePlaidIdv;
